import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import './home.css';
import HighlighterDrawOverlay from '../components/HighlighterDrawOverlay';
import WigglyHighlight from '../components/WigglyHighlight';

// Import work
import smiley from '../assets/smile.svg';
import markup from '../assets/markup.mov';
import repost from '../assets/repost.mov';
import messenger from '../assets/messenger.mov';
import yc from '../assets/yc.png';
import zoom from '../assets/zoom.mov';
import kp from '../assets/kp.jpg';
import aitransitions from '../assets/aitransitions.mov';

// Import stuff from my bag
import mybag from '../assets/mybag.png';
import mac from '../assets/mybag/mac.png';
import phone from '../assets/mybag/phone.png';
import claw from '../assets/mybag/claw.png';
import labubu from '../assets/mybag/labubu.png';
import wallet from '../assets/mybag/wallet.png';
import cables from '../assets/mybag/cables.png';
import glossier from '../assets/mybag/glossier.png';
import roundlab from '../assets/mybag/roundlab.png';
import sunnies from '../assets/mybag/sunnies.png';
import airpods from '../assets/mybag/airpods.png';
import dhc from '../assets/mybag/dhc.png';
import tide from '../assets/mybag/tide.png';

// Import SFV
import breadVideo from '../assets/sfv/bread.MOV';

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= breakpoint);
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [breakpoint]);
  return isMobile;
};

const Home = () => {
  const videoRef = useRef(null);
  const imgRef = useRef(null);
  const [video, setVideo] = useState(repost);
  const [img, setImg] = useState(null);
  const [isBlurred, setIsBlurred] = useState(false);
  const [isFinalLayout, setIsFinalLayout] = useState(false);
  const mybagContainerRef = useRef(null);
  const [currentSFV, setCurrentSFV] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [penType, setPenType] = useState('highlighter');
  const isMobile = useIsMobile(750);

  // Define all media items for keyboard navigation
  const mediaItems = [
    { id: "instagram", type: "video", src: repost },
    { id: "edits", type: "video", src: aitransitions },
    { id: "threads", type: "video", src: markup },
    { id: "messenger", type: "video", src: messenger },
    { id: "zoom", type: "video", src: zoom },
    { id: "yc", type: "image", src: yc },
    { id: "kp", type: "image", src: kp },
  ];

  // Preload mybag images
  useEffect(() => {
    const mybagImages = {
      mac: { src: mac, alt: 'MacBook' },
      phone: { src: phone, alt: 'Phone' },
      claw: { src: claw, alt: 'Claw' },
      labubu: { src: labubu, alt: 'Labubu' },
      wallet: { src: wallet, alt: 'Wallet' },
      cables: { src: cables, alt: 'Cables' },
      glossier: { src: glossier, alt: 'Glossier' },
      roundlab: { src: roundlab, alt: 'Round Lab' },
      sunnies: { src: sunnies, alt: 'Sunnies' },
      airpods: { src: airpods, alt: 'AirPods' },
      dhc: { src: dhc, alt: 'DHC' },
      tide: { src: tide, alt: 'Tide' }
    };

    Object.entries(mybagImages).forEach(([key, { src }]) => {
      const img = new Image();
      img.onload = () => {
        setLoadedImages(prev => ({
          ...prev,
          [key]: true
        }));
      };
      img.src = src;
    });
  }, []);

  useEffect(() => {
    let mouseMoveHandler;
    let timer;

    if (isBlurred) {
      timer = setTimeout(() => {
        setIsFinalLayout(true);
        const mybagItems = document.querySelectorAll('.mybag-item');
        mybagItems.forEach(item => {
          const randomDelay = Math.random() * 1.5;
          item.style.setProperty('--float-delay', `${randomDelay}s`);
        });

        mouseMoveHandler = (e) => {
          if (!mybagContainerRef.current) return;

          const containerRect = mybagContainerRef.current.getBoundingClientRect();
          const centerX = containerRect.left + containerRect.width / 2;
          const centerY = containerRect.top + containerRect.height / 2;

          const translateX = (e.clientX - centerX) * 0.01;
          const translateY = (e.clientY - centerY) * 0.01;

          mybagContainerRef.current.style.transform = 
            `translateX(${translateX}px) translateY(${translateY}px)`;
        };

        window.addEventListener('mousemove', mouseMoveHandler);
      }, 80);
    } else {
      setIsFinalLayout(false);
    }

    return () => {
      if (timer) clearTimeout(timer);
      if (mouseMoveHandler) window.removeEventListener('mousemove', mouseMoveHandler);
    };
  }, [isBlurred]);

  // Function to display media based on index
  const displayMediaByIndex = (index) => {
    const mediaItem = mediaItems[index];
    if (mediaItem.type === 'video') {
      handleMediaVisibility('video');
      setVideo(mediaItem.src);
    } else if (mediaItem.type === 'image') {
      handleMediaVisibility('image');
      setImg(mediaItem.src);
    }
  };

  // Navigation functions
  const navigateToNext = () => {
    const nextIndex = (currentMediaIndex + 1) % mediaItems.length;
    setCurrentMediaIndex(nextIndex);
    displayMediaByIndex(nextIndex);
  };

  const navigateToPrevious = () => {
    const prevIndex = (currentMediaIndex - 1 + mediaItems.length) % mediaItems.length;
    setCurrentMediaIndex(prevIndex);
    displayMediaByIndex(prevIndex);
  };

  // Keyboard event listener
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Only handle keyboard navigation when not in blurred mode
      if (isBlurred) return;
      
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        navigateToNext();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        navigateToPrevious();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMediaIndex, isBlurred]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.setAttribute('muted', '');
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => {});
    }
  }, [video]);

  function handleMediaVisibility(mediaType) {
    const videoElement = videoRef.current;
    const imgElement = imgRef.current;

    if (mediaType === 'video') {
      if (videoElement) videoElement.style.visibility = "visible";
      if (imgElement) imgElement.style.visibility = "hidden";
    } else if (mediaType === 'image') {
      if (videoElement) videoElement.style.visibility = "hidden";
      if (imgElement) imgElement.style.visibility = "visible";
    }
  }

  function handleMouseOver(e) {
    const hoveredIndex = mediaItems.findIndex(item => item.id === e.target.id);
    if (hoveredIndex !== -1) {
      setCurrentMediaIndex(hoveredIndex);
      displayMediaByIndex(hoveredIndex);
    }
  }

  function handleClick(e) {
    const videoElement = videoRef.current;
    const imgElement = imgRef.current;

    const targetIsVideo = ["threads", "instagram", "edits", "messenger", "zoom"].includes(e.target.id);
    const targetIsImage = ["yc", "kp"].includes(e.target.id);

    if (targetIsVideo && videoElement) {
      videoElement.style.transform = 'scale(1.05)';
      videoElement.style.transition = 'transform 0.2s ease-out';
      setTimeout(() => {
        videoElement.style.transform = 'scale(1)';
      }, 100);
    } else if (targetIsImage && imgElement) {
      imgElement.style.transform = 'translate(-50%, -50%) scale(1.05)';
      imgElement.style.transition = 'transform 0.2s ease-out';
      setTimeout(() => {
        imgElement.style.transform = 'translate(-50%, -50%) scale(1)';
      }, 100);
    }
  }

  function handleSFVClick(type) {
    setIsBlurred(true);
    switch(type) {
      case 'bread':
        setCurrentSFV(breadVideo);
        break;
      default:
        break;
    }
  }

  const activeId = mediaItems[currentMediaIndex]?.id;

  return (
    <div className="app">
      {!isMobile && (
        <>
          <HighlighterDrawOverlay enabled={!isBlurred} zIndex={999} penType={penType} />
          <button
            className="pen-toggle-btn"
            onClick={() => setPenType(p => p === 'highlighter' ? 'stamp' : 'highlighter')}
            title={penType === 'highlighter' ? 'Switch to stamp pen' : 'Switch to highlighter'}
          >
            {penType === 'highlighter' ? '✏️' : '🐱'}
          </button>
        </>
      )}
      {isBlurred && (
        <>
          <div className={`blur-overlay ${currentSFV ? 'sfv-mode' : ''}`} />
          <div className="inbag" onClick={() => {
            setIsBlurred(false);
            setCurrentSFV(null);
          }}>
            {currentSFV ? (
              <video 
                className="SFV"
                src={currentSFV}
                autoPlay
                muted
                loop
                playsInline
                onLoadedData={(e) => { e.target.setAttribute('muted', ''); e.target.muted = true; e.target.play().catch(() => {}); }}
              />
            ) : (
              <div className="mybag-container" ref={mybagContainerRef}>
                <img className={`mybag-item mybag-mac${isFinalLayout ? ' final-mac' : ''} ${loadedImages.mac ? 'loaded' : 'loading'}`} src={mac} alt="MacBook" />
                <img className={`mybag-item mybag-tide${isFinalLayout ? ' final-tide' : ''} ${loadedImages.tide ? 'loaded' : 'loading'}`} src={tide} alt="Tide" />
                <img className={`mybag-item mybag-dhc${isFinalLayout ? ' final-dhc' : ''} ${loadedImages.dhc ? 'loaded' : 'loading'}`} src={dhc} alt="DHC" />
                <img className={`mybag-item mybag-phone${isFinalLayout ? ' final-phone' : ''} ${loadedImages.phone ? 'loaded' : 'loading'}`} src={phone} alt="Phone" />
                <img className={`mybag-item mybag-claw${isFinalLayout ? ' final-claw' : ''} ${loadedImages.claw ? 'loaded' : 'loading'}`} src={claw} alt="Claw" />
                <img className={`mybag-item mybag-labubu${isFinalLayout ? ' final-labubu' : ''} ${loadedImages.labubu ? 'loaded' : 'loading'}`} src={labubu} alt="Labubu" />
                <img className={`mybag-item mybag-wallet${isFinalLayout ? ' final-wallet' : ''} ${loadedImages.wallet ? 'loaded' : 'loading'}`} src={wallet} alt="Wallet" />
                <img className={`mybag-item mybag-cable${isFinalLayout ? ' final-cable' : ''} ${loadedImages.cables ? 'loaded' : 'loading'}`} src={cables} alt="Cables" />
                <img className={`mybag-item mybag-glossier${isFinalLayout ? ' final-glossier' : ''} ${loadedImages.glossier ? 'loaded' : 'loading'}`} src={glossier} alt="Glossier" />
                <img className={`mybag-item mybag-roundlab${isFinalLayout ? ' final-roundlab' : ''} ${loadedImages.roundlab ? 'loaded' : 'loading'}`} src={roundlab} alt="Round Lab" />
                <img className={`mybag-item mybag-sunnies${isFinalLayout ? ' final-sunnies' : ''} ${loadedImages.sunnies ? 'loaded' : 'loading'}`} src={sunnies} alt="Sunnies" />
                <img className={`mybag-item mybag-airpods${isFinalLayout ? ' final-airpods' : ''} ${loadedImages.airpods ? 'loaded' : 'loading'}`} src={airpods} alt="AirPods" />
              </div>
            )}
          </div>
        </>
      )}
      <div>
        <h1>Kathy Zhou is a product designer at Instagram, based in Brooklyn.</h1>

        <div className="body">
          <div className="bio">
            <p>I design and *lightly code* things that help people create and share on <b id="instagram" className={`cv${activeId === 'instagram' ? ' active' : ''}`} onMouseOver={handleMouseOver} onClick={handleClick}>Instagram{activeId === 'instagram' && <WigglyHighlight />}</b> and <b id="edits" className={`cv${activeId === 'edits' ? ' active' : ''}`} onMouseOver={handleMouseOver} onClick={handleClick}>Edits{activeId === 'edits' && <WigglyHighlight />}</b>. <img id="smiley" src={smiley} alt=""/> </p>
            
            <p>In the past, I worked on <b id="threads" className={`cv${activeId === 'threads' ? ' active' : ''}`} onMouseOver={handleMouseOver} onClick={handleClick}>Threads{activeId === 'threads' && <WigglyHighlight />}</b> and <b id="messenger" className={`cv${activeId === 'messenger' ? ' active' : ''}`} onMouseOver={handleMouseOver} onClick={handleClick}>Messenger{activeId === 'messenger' && <WigglyHighlight />}</b>, helped people stay connected through the pandemic at <b id="zoom" className={`cv${activeId === 'zoom' ? ' active' : ''}`} onMouseOver={handleMouseOver} onClick={handleClick}>Zoom{activeId === 'zoom' && <WigglyHighlight />}</b>, raised money from <b id="yc" className={`cv${activeId === 'yc' ? ' active' : ''}`} onMouseOver={handleMouseOver} onClick={handleClick}>YC{activeId === 'yc' && <WigglyHighlight />}</b>, worked on food tech at Chowbus, and was a fellow at <b id="kp" className={`cv${activeId === 'kp' ? ' active' : ''}`} onMouseOver={handleMouseOver} onClick={handleClick}>Kleiner Perkins{activeId === 'kp' && <WigglyHighlight />}</b>.</p>

            <p>Fun projects:</p>
            <ul className="experiments-list">
              <li>🌅 <Link to="/sunset" className="hobbies">Sunset predictor</Link></li>
              <li>🥬 <Link to="/whatsinseason" className="hobbies">Fresh produce tracker</Link></li>
              <li>👋 <a href="https://apps.apple.com/in/app/muddy-hands-pottery-tracker/id6758968228" className="hobbies" target="_blank" rel="noopener noreferrer">Muddy Hands for potters</a></li>
              <li>🍵 <Link to="/matcha" className="hobbies">NYC's greenest matcha</Link></li>
              <li>🍞 <span onClick={() => handleSFVClick('bread')} className="hobbies">Loaves of bread</span></li>
              <li>👀 <span className="hobbies" onClick={() => setIsBlurred(true)}>Peek inside my bag</span> <img id="mybag" src={mybag} alt="" onClick={() => setIsBlurred(true)}/></li>
            </ul>

            <div className="socials">
              <p><a className="social-link" href="https://www.twitter.com/kathytzhou" target="_blank" rel="noopener noreferrer">TW ↗</a>
              <a className="social-link" href="https://instagram.com/kathytzhou" target="_blank" rel="noopener noreferrer">IG ↗</a>
              <a className="social-link" href="https://www.linkedin.com/in/kathytzhou" target="_blank" rel="noopener noreferrer">LI ↗</a>
              <a className="social-link" href="mailto:kathytzhou@gmail.com">SAY HI ↗</a></p>
            </div>
          </div>

          {!isMobile && (
            <div className="work">
              <video id="video" loop muted autoPlay playsInline src={video} ref={videoRef} onLoadedData={(e) => { e.target.setAttribute('muted', ''); e.target.muted = true; e.target.play().catch(() => {}); }} ></video>
              <img id="img" src={img} ref={imgRef} alt=""/>
            </div>
          )}
        </div>

        {isMobile && (
          <div className="work mobile-work">
            <video id="video" loop muted autoPlay playsInline src={video} ref={videoRef} onLoadedData={(e) => { e.target.setAttribute('muted', ''); e.target.muted = true; e.target.play().catch(() => {}); }} ></video>
            <img id="img" src={img} ref={imgRef} alt=""/>
          </div>
        )}

      </div>
    </div>
  );
};

export default Home;
